/* eslint-disable @typescript-eslint/no-explicit-any */
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import {
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { ReactNode, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { EFielRefundSortBy, EOrderSortBy, SortBy } from "@/types";

interface IProps<T> {
  rows: Array<T>;
  labels: Array<string | ReactNode>;
  page?: number;
  count?: number;
  onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  setSort?: ({ column, direction }: SortBy) => void;
}

const RefundTable = <T,>({
  rows,
  labels,
  page = 1,
  count = 1,
  onChange,
  setSort,
}: IProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [activeColumn, setActiveColumn] = useState<boolean>(true);

  const handleSort = (column: string) => {
    if (!setSort) return;
    if (activeColumn) {
      setSort({ column, direction: EOrderSortBy.ASC });
    } else {
      setSort({ column, direction: EOrderSortBy.DESC });
    }
  };

  const transformLabelToField = (label: string): string => {
    const fieldMap: Record<string, string> = {
      "Amount of refund": EFielRefundSortBy.AMOUNT_OF_REFUND,
      "Request Date": EFielRefundSortBy.REQUEST_DATE,
    };

    return fieldMap[label] || label;
  };

  return (
    <div className="mb-2 flex w-full flex-col overflow-hidden">
      <TableContainer
        ref={tableContainerRef}
        className="rounded-[5px] pb-6 [&::-webkit-scrollbar-button:end]:w-48 [&::-webkit-scrollbar-button:start]:w-48 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#94A3B8] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-button-background [&::-webkit-scrollbar]:h-4"
      >
        <Table stickyHeader aria-label="sticky table text-[#020617]">
          <TableHead className="bg-[#5248E3]">
            <TableRow>
              {labels.map((label, index) => {
                const labelStr = typeof label === "string" ? label : "";
                const isSortable =
                  labelStr === "Amount of refund" ||
                  labelStr === "Request Date";

                return (
                  <TableCell
                    className="h-[42px] bg-[#5248E3] py-0 text-[12px]"
                    key={index}
                    style={{
                      backgroundColor: "#5248E3",
                    }}
                  >
                    {isSortable ? (
                      <div
                        onClick={() => {
                          setActiveColumn(!activeColumn);
                          handleSort(transformLabelToField(labelStr));
                        }}
                        className="flex cursor-pointer items-center justify-between gap-1"
                      >
                        <p className="whitespace-nowrap font-semibold text-white">
                          {label}
                        </p>
                        <img src="/images/chevron-refund.png" alt="arrow" />
                      </div>
                    ) : (
                      <p
                        className={`whitespace-nowrap font-semibold text-white ${labelStr === "Action" ? "text-center" : ""}`}
                      >
                        {label}
                      </p>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, indexRow) => (
                <TableRow
                  key={indexRow}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {Object.keys(row as object).map((keyRow, index) => {
                    return (
                      <TableCell
                        className={`bg-white text-[12px] text-primary`}
                        key={index}
                        component="th"
                        scope="row"
                      >
                        {(row as any)[keyRow]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={labels.length}
                  align="center"
                  className="bg-white"
                >
                  <div className="flex flex-col items-center py-6">
                    <FolderOffOutlinedIcon
                      fontSize="large"
                      className="text-gray"
                    />
                    <p className="mt-2 font-medium text-gray">Data not found</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!!count && (
        <Pagination
          shape="rounded"
          className="my-[12px] mt-[24px] self-center"
          count={count}
          page={page}
          onChange={onChange}
          siblingCount={0}
          boundaryCount={3}
          renderItem={(item) => (
            <PaginationItem
              slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
              sx={{
                backgroundColor: "transparent",
                color: "#94A3B8",
                fontSize: 16,
              }}
              {...item}
            />
          )}
        />
      )}
    </div>
  );
};

export default RefundTable;
